import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { ArrowRight, CheckCircle2, Download, FileText, Monitor, RefreshCw } from 'lucide-react';
import { Badge, buttonClasses, Card, EmptyState, ErrorState, LoadingState, SectionHeader } from '@pixlide/ui';
import { api } from '@/api/client';
import { queryClient, queryKeys } from '@/api/query-client';
import { formatCurrency, formatDate } from '@/lib/format';
import { useInvoices, useSubscription } from '@/api/queries/dashboard';
import {
  CancelSubscriptionButton,
  ResumeSubscriptionButton,
  SUBSCRIPTION_STATUS_PENDING_CANCELLATION,
} from '@/features/billing/SubscriptionActions';

/**
 * Statut d'une facture, dérivé des dates plutôt que de l'enum brute.
 *
 * `InvoiceStatus` porte des valeurs internes (brouillon, émise, en litige…) qui ne
 * correspondent pas toutes à une distinction utile pour le client : ce qui l'intéresse,
 * c'est de savoir si elle est payée, en retard, ou en attente.
 */
function invoiceBadge(invoice: { paidAt?: string | null; dueAt?: string | null }): {
  label: string;
  tone: 'success' | 'warning' | 'neutral';
} {
  if (invoice.paidAt) {
    return { label: 'Payée', tone: 'success' };
  }

  if (invoice.dueAt && new Date(invoice.dueAt) < new Date()) {
    return { label: 'En retard', tone: 'warning' };
  }

  return { label: 'En attente', tone: 'neutral' };
}

/**
 * TVA française sur prestation de service, dupliquée depuis `Invoice.DefaultVatRate`
 * (backend/src/Pixlide.Domain/Billing/Invoice.cs) : le prix d'une formule est HT, et c'est
 * ce même taux que le backend applique pour calculer le TTC de chaque facture. Ce n'est donc
 * pas une valeur inventée côté front, seulement la même constante métier qu'ailleurs.
 */
const VAT_RATE = 0.2;

/** En-tête de carte : icône dans un carré arrondi + libellé en petites capitales, comme sur la maquette. */
function CardEyebrow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="grid size-8 shrink-0 place-items-center rounded-xl bg-ink-50 text-ink-700">{icon}</span>
      <span className="text-xs font-medium tracking-wide text-ink-500 uppercase">{children}</span>
    </div>
  );
}

function useDownloadInvoice() {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const { data } = await api.POST('/api/v1/documents/{documentId}/download-url', {
        params: { path: { documentId } },
      });

      return data!;
    },

    onSuccess: (result) => {
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
    },
  });
}

/**
 * Transactions : abonnement en cours et historique de facturation.
 *
 * La maquette affiche aussi une carte bancaire enregistrée ; ce détail n'a pas
 * d'équivalent dans /billing/subscription (pas de moyen de paiement exposé par l'API à ce
 * jour) et n'est donc pas reproduit pour éviter d'afficher une donnée inventée.
 */
export function TransactionsPage() {
  const { checkout } = useSearch({ from: '/app/transactions' });
  const subscription = useSubscription();
  const invoices = useInvoices({ pageSize: 10 });
  const downloadInvoice = useDownloadInvoice();

  /**
   * Retour de Stripe Checkout : l'abonnement n'est PAS encore forcément à jour à l'arrivée
   * sur cette page. Stripe redirige le navigateur dès le paiement confirmé, mais applique
   * l'abonnement via un webhook asynchrone (`ProcessStripeWebhookEventCommand`), qui peut
   * arriver une ou deux secondes après. On raccourcit cette attente en réinterrogeant
   * l'abonnement toutes les 2 secondes pendant 15 secondes plutôt que de laisser
   * l'utilisateur sur une carte visiblement périmée.
   */
  useEffect(() => {
    if (checkout !== 'success') return;

    const interval = setInterval(() => {
      void queryClient.refetchQueries({ queryKey: queryKeys.subscription });
    }, 2000);

    const timeout = setTimeout(() => clearInterval(interval), 15_000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [checkout]);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8">
      <SectionHeader title="Transactions" />

      {checkout === 'success' ? (
        <Card className="flex items-center gap-3 border border-success-100 bg-success-50 p-4">
          <CheckCircle2 aria-hidden className="size-5 shrink-0 text-success-500" />
          <p className="text-sm text-ink-700">
            Paiement confirmé. Votre abonnement est en cours d'activation — cette page se met à
            jour automatiquement dans les prochaines secondes.
          </p>
        </Card>
      ) : null}

      <section aria-labelledby="subscription-heading">
        <h2 id="subscription-heading" className="sr-only">
          Abonnement
        </h2>

        {subscription.isPending ? <LoadingState /> : null}

        {subscription.isError ? (
          <ErrorState
            message="Impossible de charger votre abonnement pour le moment."
            onRetry={() => void subscription.refetch()}
          />
        ) : null}

        {subscription.data ? (
          (() => {
            const isPendingCancellation = subscription.data.status === SUBSCRIPTION_STATUS_PENDING_CANCELLATION;

            // Distincte de currentPeriodEnd quand la formule impose un engagement encore en
            // cours : c'est la date à laquelle le service s'arrêtera réellement (voir
            // Subscription.EffectiveCancellationDate côté backend).
            const serviceEndsAt =
              subscription.data.commitmentEndsAt && subscription.data.commitmentEndsAt > subscription.data.currentPeriodEnd
                ? subscription.data.commitmentEndsAt
                : subscription.data.currentPeriodEnd;

            return (
              <Card className="flex flex-col gap-5 p-6">
                <div className="flex items-start justify-between gap-2">
                  <CardEyebrow icon={<RefreshCw aria-hidden className="size-4" />}>Abonnement</CardEyebrow>
                  {isPendingCancellation ? (
                    <Badge tone="warning">Résiliation programmée</Badge>
                  ) : (
                    <Badge tone="success">Actif</Badge>
                  )}
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-5xl font-semibold tracking-tight">{subscription.data.planName}</p>

                    <div className="mt-3 flex flex-col gap-1.5 text-sm text-ink-500">
                      <span className="flex items-center gap-2">
                        <Monitor aria-hidden className="size-4 text-ink-300" />
                        {subscription.data.activeScreens}/{subscription.data.screenAllowance} écrans utilisés
                      </span>
                      <span className="flex items-center gap-2">
                        <RefreshCw aria-hidden className="size-4 text-ink-300" />
                        {isPendingCancellation ? (
                          <>
                            Service actif jusqu'au{' '}
                            <span className="font-medium text-ink-700">{formatDate(serviceEndsAt)}</span>
                          </>
                        ) : (
                          <>
                            Renouvellement le{' '}
                            <span className="font-medium text-ink-700">{formatDate(serviceEndsAt)}</span>
                          </>
                        )}
                      </span>
                    </div>

                    {isPendingCancellation
                    && subscription.data.commitmentEndsAt
                    && subscription.data.commitmentEndsAt > subscription.data.currentPeriodEnd ? (
                      <p className="mt-2 text-xs text-ink-500">
                        Cette formule est engagée : le service se poursuit jusqu'au terme de
                        l'engagement, au-delà de la période de facturation en cours.
                      </p>
                    ) : null}
                  </div>

                  <img
                    src="/illustrations/subscription-coins.svg"
                    alt=""
                    aria-hidden
                    className="hidden h-auto w-32 shrink-0 sm:block"
                  />
                </div>

                <div className="flex flex-wrap items-end justify-between gap-4 border-t border-ink-100/70 pt-5">
                  <div className="flex flex-col gap-2">
                    <p className="text-xs text-ink-500">
                      Abonnement <span className="text-ink-300">mensuel</span>
                    </p>

                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to="/offres"
                        className={buttonClasses({ variant: 'secondary', size: 'md', className: 'rounded-xl justify-between gap-8' })}
                      >
                        Gérer mon abonnement
                        <ArrowRight aria-hidden className="size-4" />
                      </Link>

                      {isPendingCancellation ? <ResumeSubscriptionButton /> : <CancelSubscriptionButton />}
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-3xl font-semibold tracking-tight">
                      {formatCurrency(subscription.data.monthlyPrice)}
                      <span className="text-base font-normal text-ink-500"> HT / mois</span>
                    </p>
                    <p className="text-sm text-ink-500">
                      {formatCurrency(subscription.data.monthlyPrice * (1 + VAT_RATE))} TTC / mois
                    </p>
                  </div>
                </div>
              </Card>
            );
          })()
        ) : null}
      </section>

      <section aria-labelledby="invoices-heading">
        <SectionHeader title="Factures" />
        <h2 id="invoices-heading" className="sr-only">
          Factures
        </h2>

        {invoices.isPending ? <LoadingState /> : null}

        {invoices.isError ? (
          <ErrorState
            message="Impossible de charger vos factures pour le moment."
            onRetry={() => void invoices.refetch()}
          />
        ) : null}

        {invoices.data?.items.length === 0 ? (
          <EmptyState
            icon={<FileText className="size-10" strokeWidth={1.25} />}
            title="Aucune facture"
            description="Vos futures factures apparaîtront ici dès leur émission."
          />
        ) : null}

        {invoices.data && invoices.data.items.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {invoices.data.items.map((invoice) => {
              const badge = invoiceBadge(invoice);

              return (
                <Card key={invoice.id} className="flex flex-col gap-4 p-5">
                  <div className="flex items-center justify-between gap-2">
                    <CardEyebrow icon={<FileText aria-hidden className="size-4" />}>Facture</CardEyebrow>
                    <span className="text-xs font-medium text-ink-300">#{invoice.number}</span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-ink-500">
                      {invoice.issuedAt ? formatDate(invoice.issuedAt) : '—'} · {formatCurrency(invoice.totalAmount)}
                    </p>
                    <Badge tone={badge.tone}>{badge.label}</Badge>
                  </div>

                  <button
                    type="button"
                    disabled={!invoice.documentId || downloadInvoice.isPending}
                    onClick={() => invoice.documentId && downloadInvoice.mutate(invoice.documentId)}
                    className={buttonClasses({ variant: 'secondary', size: 'md' }) + ' mt-auto justify-center'}
                  >
                    Télécharger
                    <Download aria-hidden className="size-4" />
                  </button>
                </Card>
              );
            })}
          </div>
        ) : null}
      </section>
    </div>
  );
}
